// src/pages/CargaMasiva/PlantillasCargaMasiva.js
//
// Gestor propio de plantillas de Carga Masiva (las 11 seeded).
// - Lista todas las plantillas activas
// - Click en una abre editor TinyMCE a pantalla completa
// - Guardar actualiza Contenido_HTML y bump de Version

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Box, Paper, Typography, Button, IconButton, Tooltip,
    Table, TableBody, TableRow, TableCell, TableContainer, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, TextField, Alert,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    Visibility as VisibilityIcon,
    Description as DescriptionIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import TinyEditor from '../../components/TinyEditor';
import { usePermisos } from '../../utils/usePermisos';
import {
    cargaMasivaListarPlantillasJWT,
    cargaMasivaObtenerPlantillaJWT,
    cargaMasivaActualizarPlantillaJWT,
} from '../../api/endpoints';

const COLOR = '#6a1b9a';
const BR = 4;

const GRUPOS = {
    CARGA_MASIVA_CARTA_PODER_TRIB: { grupo: 'Carta Poder', variant: 'Tributarias', color: '#5e35b1' },
    CARGA_MASIVA_CARTA_PODER_NO_TRIB: { grupo: 'Carta Poder', variant: 'No Tributarias', color: '#5e35b1' },
    CARGA_MASIVA_DEMANDA_TRIB: { grupo: 'Demanda', variant: 'Tributarias (DGR)', color: '#c2185b' },
    CARGA_MASIVA_DEMANDA_NO_TRIB_MULTA: { grupo: 'Demanda', variant: 'No Trib — Multa (Juzgado Paz/Producción)', color: '#c2185b' },
    CARGA_MASIVA_DEMANDA_NO_TRIB_REGUL: { grupo: 'Demanda', variant: 'No Trib — Regulatoria (Tránsito/Ambiente/Trabajo)', color: '#c2185b' },
    CARGA_MASIVA_DEMANDA_NO_TRIB_CONSUM: { grupo: 'Demanda', variant: 'No Trib — Defensa Consumidor', color: '#c2185b' },
    CARGA_MASIVA_DEMANDA_NO_TRIB_CEJUME: { grupo: 'Demanda', variant: 'No Trib — CEJUME', color: '#c2185b' },
    CARGA_MASIVA_RECIBO_TRIB_INDIVIDUAL: { grupo: 'Recibo', variant: 'Tributarias — Individual', color: '#00897b' },
    CARGA_MASIVA_RECIBO_TRIB_CONJUNTO: { grupo: 'Recibo', variant: 'Tributarias — Conjunto', color: '#00897b' },
    CARGA_MASIVA_RECIBO_NO_TRIB_INDIVIDUAL: { grupo: 'Recibo', variant: 'No Tributarias — Individual', color: '#00897b' },
    CARGA_MASIVA_RECIBO_NO_TRIB_CONJUNTO: { grupo: 'Recibo', variant: 'No Tributarias — Conjunto', color: '#00897b' },
};

const PLACEHOLDERS_DOCS = {
    CARTA_PODER: ['CARTA_PODER_PREFIJO', 'CARTA_PODER_NUMERO_PAD', 'CARTA_PODER_ANO_YY', 'APODERADOS_LISTA', 'DEMANDADO', 'CONCEPTO_DEUDA', 'FECHA'],
    DEMANDA: ['APODERADOS_LISTA', 'REPARTICION', 'REPARTICION_DOMICILIO', 'DEMANDADO', 'DEMANDADO_DOMICILIO', 'MONTO_NUM', 'MONTO_LETRAS', 'CERTIFICADO_NUMERO'],
    RECIBO: ['ABOGADO_PRINCIPAL', 'ABOGADO_PRINCIPAL_MAT', 'LOTE_ID', 'EXPEDIENTE_ADM', 'CO_APODERADOS', 'FECHA', 'ANO_YYYY', 'TABLA_FILAS'],
};

export default function PlantillasCargaMasiva() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { tienePermiso } = usePermisos();

    const puedeEditar = tienePermiso('CARGA MASIVA', 'UPDATE');

    const [plantillas, setPlantillas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editando, setEditando] = useState(null);
    const [previewHtml, setPreviewHtml] = useState(null);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await cargaMasivaListarPlantillasJWT();
            if (r?.ok) setPlantillas(r.plantillas || []);
            else enqueueSnackbar(r?.mensaje || 'Error al listar', { variant: 'error' });
        } finally { setLoading(false); }
    }, [enqueueSnackbar]);

    useEffect(() => { cargar(); }, [cargar]);

    // Agrupa por "grupo" para mostrarlas ordenadas
    const porGrupo = plantillas.reduce((acc, p) => {
        const cfg = GRUPOS[p.Categoria] || { grupo: 'Otras', variant: p.Categoria, color: '#666' };
        if (!acc[cfg.grupo]) acc[cfg.grupo] = [];
        acc[cfg.grupo].push({ ...p, _meta: cfg });
        return acc;
    }, {});

    return (
        <Box sx={{ bgcolor: '#f6f8fc', minHeight: '100vh', py: 3, px: { xs: 2, md: 3 } }}>
            <Container maxWidth="lg" disableGutters>
                <Paper sx={{ p: 2.5, mb: 2, borderRadius: `${BR}px`, borderLeft: `4px solid ${COLOR}`, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate('/gestion-lotes?tab=carga-masiva')} size="small"><ArrowBackIcon /></IconButton>
                    <DescriptionIcon sx={{ color: COLOR, fontSize: 36 }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: COLOR, lineHeight: 1.1 }}>Plantillas de Carga Masiva</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Editá el HTML de cada plantilla. Los placeholders <strong>{'{{NOMBRE}}'}</strong> se reemplazan en runtime.
                        </Typography>
                    </Box>
                </Paper>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: COLOR }} /></Box>
                ) : Object.entries(porGrupo).map(([grupo, items]) => (
                    <Paper key={grupo} sx={{ mb: 2, borderRadius: `${BR}px`, overflow: 'hidden' }}>
                        <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(106,27,154,0.05)', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLOR }}>{grupo}</Typography>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                    {items.map(p => (
                                        <TableRow key={p.Id} hover>
                                            <TableCell sx={{ width: 40 }}>
                                                <Box sx={{ width: 8, height: 32, bgcolor: p._meta.color, borderRadius: 1 }} />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{p._meta.variant}</Typography>
                                                <Typography variant="caption" color="text.secondary">{p.Descripcion}</Typography>
                                            </TableCell>
                                            <TableCell align="center"><Chip size="small" label={`v${p.Version}`} variant="outlined" /></TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Ver HTML renderizado">
                                                    <IconButton size="small" onClick={() => setPreviewHtml({ html: p.Contenido_HTML || '', nombre: p._meta.variant })}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {puedeEditar && (
                                                    <Tooltip title="Editar">
                                                        <IconButton size="small" onClick={() => setEditando(p)}>
                                                            <EditIcon fontSize="small" sx={{ color: COLOR }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                ))}
            </Container>

            {editando && (
                <EditarPlantillaDialog
                    plantilla={editando}
                    onClose={() => setEditando(null)}
                    onSaved={() => { setEditando(null); cargar(); }}
                />
            )}

            {previewHtml && (
                <PreviewHtmlDialog
                    html={previewHtml.html}
                    nombre={previewHtml.nombre}
                    onClose={() => setPreviewHtml(null)}
                />
            )}
        </Box>
    );
}

// ============================================================================
// Dialog editar plantilla (TinyMCE)
// ============================================================================
function EditarPlantillaDialog({ plantilla, onClose, onSaved }) {
    const { enqueueSnackbar } = useSnackbar();
    const [nombre, setNombre] = useState(plantilla.Nombre || '');
    const [descripcion, setDescripcion] = useState(plantilla.Descripcion || '');
    const [fullData, setFullData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const htmlRef = useRef(plantilla.Contenido_HTML || '');

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const r = await cargaMasivaObtenerPlantillaJWT(plantilla.Id);
                if (r?.ok) {
                    setFullData(r.plantilla);
                    htmlRef.current = r.plantilla.Contenido_HTML || '';
                }
            } finally { setLoading(false); }
        })();
    }, [plantilla.Id]);

    const guardar = async () => {
        setGuardando(true);
        try {
            const r = await cargaMasivaActualizarPlantillaJWT(plantilla.Id, {
                nombre, descripcion,
                contenido_html: htmlRef.current,
            });
            if (r?.ok) {
                enqueueSnackbar('Plantilla guardada', { variant: 'success' });
                onSaved?.();
            } else {
                enqueueSnackbar(r?.mensaje || 'Error al guardar', { variant: 'error' });
            }
        } finally { setGuardando(false); }
    };

    // Detectar tipo para mostrar placeholders sugeridos
    const tipoDoc =
        plantilla.Categoria.includes('CARTA_PODER') ? 'CARTA_PODER' :
        plantilla.Categoria.includes('DEMANDA') ? 'DEMANDA' :
        plantilla.Categoria.includes('RECIBO') ? 'RECIBO' : null;
    const placeholders = tipoDoc ? PLACEHOLDERS_DOCS[tipoDoc] : [];

    return (
        <Dialog open onClose={onClose} maxWidth={false}
            PaperProps={{ sx: { width: '90vw', height: '92vh', borderRadius: `${BR}px` } }}>
            <DialogTitle sx={{ bgcolor: COLOR, color: 'white', py: 1.5 }}>Editar plantilla</DialogTitle>
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ p: 2, display: 'flex', gap: 2, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                    <TextField size="small" label="Nombre" value={nombre} onChange={e => setNombre(e.target.value)} sx={{ flex: 1 }} />
                    <TextField size="small" label="Descripción" value={descripcion} onChange={e => setDescripcion(e.target.value)} sx={{ flex: 2 }} />
                </Box>
                {placeholders.length > 0 && (
                    <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(106,27,154,0.04)', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: COLOR }}>Placeholders disponibles: </Typography>
                        {placeholders.map(p => (
                            <Chip key={p} size="small" label={`{{${p}}}`} sx={{ ml: 0.5, fontFamily: 'monospace', fontSize: '0.7rem' }} />
                        ))}
                    </Box>
                )}
                <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#fafafa' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: COLOR }} /></Box>
                    ) : fullData ? (
                        <TinyEditor
                            key={plantilla.Id}
                            id={`tiny-plantilla-${plantilla.Id}`}
                            initialHtml={fullData.Contenido_HTML || ''}
                            onChange={(html) => { htmlRef.current = html; }}
                        />
                    ) : <Alert severity="error">No se pudo cargar la plantilla</Alert>}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={guardando}>Cancelar</Button>
                <Button variant="contained" onClick={guardar} disabled={guardando || loading}
                    sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' } }}>
                    {guardando ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Guardar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ============================================================================
// Dialog preview HTML
// ============================================================================
function PreviewHtmlDialog({ html, nombre, onClose }) {
    return (
        <Dialog open onClose={onClose} maxWidth={false}
            PaperProps={{ sx: { width: '80vw', height: '85vh', borderRadius: `${BR}px` } }}>
            <DialogTitle sx={{ bgcolor: COLOR, color: 'white', py: 1.5 }}>
                Preview: {nombre}
            </DialogTitle>
            <DialogContent sx={{ p: 3, bgcolor: '#f5f5f5' }}>
                <Paper sx={{ p: 4, maxWidth: '21cm', mx: 'auto', minHeight: '29.7cm' }}>
                    <div dangerouslySetInnerHTML={{ __html: html }} />
                </Paper>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
}
